/**
 * Support page — launch-safe minimum.
 * Shown at /support. Renders contact info + safe navigation actions
 * whether the visitor is signed in or not.
 */
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LifeBuoy, Mail, LogIn, LogOut, LayoutDashboard, ArrowLeft } from 'lucide-react';
import axios from 'axios';
import theme from '../theme';

const SUPPORT_EMAIL = 'support@kickbackcontract.com';

export default function SupportPage({ user, setUser }) {
  const navigate = useNavigate();

  const dashboardPathFor = (role) => {
    switch (role) {
      case 'creator':   return '/creator/dashboard';
      case 'broker':    return '/broker/dashboard';
      case 'buyer':     return '/buyer/dashboard';
      case 'admin':     return '/admin/dashboard';
      case 'architect': return '/architect/profile';
      default:          return '/';
    }
  };

  const handleSignOut = () => {
    try {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    } catch (_) { /* ignore */ }
    if (typeof setUser === 'function') setUser(null);
    navigate('/auth');
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: theme.colors.bg.page,
      padding: 24,
    }}>
      <div
        data-testid="support-card"
        style={{
          maxWidth: 560, width: '100%', padding: 32,
          background: theme.colors.bg.card,
          border: `1px solid ${theme.colors.borders}`,
          borderRadius: theme.radius.card,
          boxShadow: theme.colors.shadows.md,
        }}
      >
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '4px 10px', borderRadius: 999,
          background: '#eff6ff', color: '#1e40af',
          fontSize: 12, fontWeight: 700, marginBottom: 12,
        }}>
          <LifeBuoy size={14} /> Kickback Contract Support
        </div>

        <h1
          data-testid="support-title"
          style={{ fontSize: 26, fontWeight: 800, margin: 0, marginBottom: 8, color: theme.colors.text.primary }}
        >
          Contact Kickback Contract Support
        </h1>
        <p style={{ color: theme.colors.text.secondary, marginBottom: 20, lineHeight: 1.5 }}>
          Need help accessing your account or completing setup? Contact us and
          include the email used at checkout so we can look up your membership
          quickly.
        </p>

        <a
          data-testid="support-email-link"
          href={`mailto:${SUPPORT_EMAIL}?subject=Kickback%20Contract%20Support`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 10,
            padding: '12px 20px', marginBottom: 16,
            borderRadius: theme.radius.cta,
            background: theme.colors.primary, color: '#fff',
            fontWeight: 800, fontSize: 14, textDecoration: 'none',
          }}
        >
          <Mail size={14} /> Email {SUPPORT_EMAIL}
        </a>

        <div
          data-testid="support-email-plain"
          style={{
            fontSize: 13, color: theme.colors.text.muted, marginBottom: 22,
          }}
        >
          Or copy directly: <strong>{SUPPORT_EMAIL}</strong>
        </div>

        {user ? (
          <div
            data-testid="support-signed-in-block"
            style={{
              padding: 14, marginBottom: 14,
              background: '#f8fafc', border: `1px solid ${theme.colors.borders}`,
              borderRadius: 10,
            }}
          >
            <div style={{ fontSize: 12, color: theme.colors.text.muted, marginBottom: 4 }}>
              You are signed in as
            </div>
            <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>
              {user.email} <span style={{ color: theme.colors.text.muted, fontWeight: 500 }}>({user.role})</span>
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button
                data-testid="support-goto-dashboard"
                onClick={() => navigate(dashboardPathFor(user.role))}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', border: 'none',
                  borderRadius: 8, background: theme.colors.primary,
                  color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                }}
              >
                <LayoutDashboard size={13} /> Return to my dashboard
              </button>
              <button
                data-testid="support-signout"
                onClick={handleSignOut}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px',
                  border: `1px solid ${theme.colors.borders}`,
                  borderRadius: 8, background: '#fff',
                  color: theme.colors.text.primary,
                  fontWeight: 700, fontSize: 13, cursor: 'pointer',
                }}
              >
                <LogOut size={13} /> Sign out
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
            <Link
              data-testid="support-back-to-login"
              to="/auth"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px',
                border: `1px solid ${theme.colors.borders}`,
                borderRadius: 8, background: '#fff',
                color: theme.colors.text.primary,
                fontWeight: 700, fontSize: 13, textDecoration: 'none',
              }}
            >
              <LogIn size={13} /> Back to login
            </Link>
            <Link
              data-testid="support-back-home"
              to="/"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                padding: '8px 14px',
                border: `1px solid ${theme.colors.borders}`,
                borderRadius: 8, background: '#fff',
                color: theme.colors.text.primary,
                fontWeight: 700, fontSize: 13, textDecoration: 'none',
              }}
            >
              <ArrowLeft size={13} /> Home
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
